// =========================================================================
// Locked content from the OneKey Consulting one-pager. DO NOT paraphrase.
// =========================================================================

export const BRAND = {
  name: "Wanakee Trask",
  title: "School Climate & Community Partnership Consultant",
  rolesLine: "KEYNOTE SPEAKER | FOUNDER | ARMY VETERAN",
  contactEmail: "consult@wanakeetrask.com",
  website: "wanakeetrask.com",
  elevateGreatnessUrl: "https://www.elevategreatnesschi.org",
  pressArticleUrl:
    "http://southwestregionalpublishing.com/2025/05/24/chicago-leader-reshapes-futures-through-elevate-greatness/",
};

export const NAV_LINKS = [
  { label: "Home", to: "/" },
  { label: "About", to: "/about" },
  { label: "Services", to: "/services" },
  { label: "Media", to: "/media" },
];

// Hero — verbatim premise from one-pager
export const HERO = {
  headline: "School climate is not soft work. It is protective infrastructure.",
  // Rotating sub-line items — each names a problem she solves and who she helps,
  // pulled and condensed from one-pager language.
  rotators: [
    "For district and school leaders strengthening school climate and student belonging.",
    "For schools translating climate priorities into durable systems their teams can sustain.",
    "For leaders building the relationship-based safety conditions early intervention depends on.",
    "For districts replacing program patchwork with coordinated community partnership infrastructure.",
    "For administrators moving initiatives from compliance and documentation toward consistent practice.",
  ],
  primaryCta: "Book a Consultation",
  secondaryCta: "Explore Services",
};

export const BELIEF =
  "Attendance, behavior, staff morale, family engagement, and student safety move with the strength of the relationships beneath them. Wanakee helps schools translate that understanding into durable systems their teams can implement, monitor, and sustain.";

export const ROLES = [
  "Founder and Executive Director, Elevate Greatness",
  "Founder, Don't Sleep On Chicago Kids",
  "Army National Guard Veteran",
  "Former Classroom Teacher and Basketball Coach",
  "Keynote Speaker and Panelist",
];

export const ABOUT_BIO_PARAGRAPHS = [
  "Wanakee Trask is an educator, consultant, and keynote speaker with more than a decade of experience across education, youth development, school and community partnerships, and stakeholder engagement. She works with district and school leaders to strengthen school climate, student belonging, and the community partnerships that sustain student success.",
  "Her work is anchored in a clear professional premise. School climate is not soft work. It is protective infrastructure. Attendance, behavior, staff morale, family engagement, and student safety move with the strength of the relationships beneath them. Wanakee helps schools translate that understanding into durable systems their teams can implement, monitor, and sustain.",
];

// 5 Areas of Expertise — long-form verbatim from one-pager.
export const AREAS_OF_EXPERTISE = [
  {
    id: "climate-belonging",
    title: "School Climate & Belonging Strategy",
    short: "Treat school climate as measurable Tier 1 infrastructure, not seasonal effort.",
    long:
      "Wanakee partners with district and school leaders to treat school climate as measurable Tier 1 infrastructure rather than a series of one-time events. She helps teams design and embed systems of belonging across the school year and align them with attendance, behavior, and engagement data, so connectedness becomes consistent practice rather than seasonal effort. Her work strengthens the relational conditions that climate and culture surveys measure and that drive student engagement, educator morale, and school-wide culture.",
    problem:
      "Climate work is treated as one-time events disconnected from attendance, behavior, and engagement data.",
    builds:
      "Systems of belonging embedded across the school year, aligned to the data that already drives decisions.",
    outcome:
      "Consistent practice that strengthens student engagement, educator morale, and school-wide culture.",
    audiences: ["schools", "districts", "community", "organizations", "company"],
  },
  {
    id: "safety-voice",
    title: "Relationship-Based Safety & Student Voice",
    short: "The relational layer of student safety that formal systems rely on but cannot manufacture.",
    long:
      "Wanakee strengthens the relational layer of student safety that formal systems rely on but cannot manufacture: trusted-adult relationships, adult awareness, student voice, and clear pathways for concerns to reach the right supports. She helps schools build cultures in which students are known and adults are positioned to notice early indicators, reinforcing existing reporting and intervention structures with the human conditions that make them effective. This is protective infrastructure that strengthens early intervention, student trust, and connectedness.",
    problem:
      "Reporting and intervention systems exist, but the trusted-adult conditions that make them effective are missing.",
    builds:
      "Cultures where students are known, voice is heard, and concerns reach the right supports.",
    outcome:
      "Stronger early intervention, student trust, and protective connectedness.",
    audiences: ["schools", "districts"],
  },
  {
    id: "partnerships",
    title: "School-Community Partnership Development",
    short: "Coordinated partnership infrastructure in place of a patchwork of disconnected programs.",
    long:
      "Wanakee helps districts and schools build coordinated partnership infrastructure in place of a patchwork of disconnected programs. She supports leaders in identifying, onboarding, and activating community organizations, higher education partners, businesses, and families, aligning each to defined student and family needs and to a clear role within the school's system of support. Her approach moves community engagement from episodic events toward an accountable, sustainable network of wraparound support.",
    problem:
      "Community engagement happens in episodic events, with partners unsure of their role in the school's system of support.",
    builds:
      "Onboarding, activation, and alignment of community, higher education, business, and family partners to defined needs.",
    outcome:
      "An accountable, sustainable network of wraparound support around students and families.",
    audiences: ["schools", "districts", "organizations", "community", "company"],
  },
  {
    id: "facilitation",
    title: "Stakeholder Facilitation & Team Building",
    short: "Adult conditions that implementation depends on: trust, shared language, role clarity, aligned priorities.",
    long:
      "Wanakee facilitates professional development and strategic planning for administrators, educators, school teams, and community partners. Her sessions build the adult conditions that implementation depends on: trust, shared language, role clarity, and aligned priorities. She helps teams move initiatives from compliance and documentation toward consistent, lived practice, strengthening staff cohesion and readiness to carry the work forward.",
    problem:
      "Initiatives stall at compliance and documentation, never reaching consistent, lived practice.",
    builds:
      "Facilitated professional development and strategic planning that build trust, shared language, and role clarity.",
    outcome:
      "Teams that carry the work forward with cohesion and readiness.",
    audiences: ["schools", "districts", "organizations", "community", "company"],
  },
  {
    id: "program-design",
    title: "Program Design & Implementation Support",
    short: "Translate district and school priorities into structured, implementable, sustainable programs.",
    long:
      "Wanakee translates district and school priorities into structured, implementable programs. She designs leadership pipelines, school-based workshops, mentorship and volunteer structures, and family and community engagement initiatives, each with rollout plans that define roles, sequencing, and measures of follow-through. Her emphasis on fidelity and sustainability produces programs a school team can implement, monitor, and maintain without dependence on any single person.",
    problem:
      "Priorities sit on paper, with no clear rollout, roles, sequencing, or measures of follow-through.",
    builds:
      "Leadership pipelines, workshops, mentorship and volunteer structures, and family and community engagement initiatives with full rollout plans.",
    outcome:
      "Programs a team can implement, monitor, and maintain without dependence on any single person.",
    audiences: ["schools", "districts", "organizations", "community", "company"],
  },
];

export const SIGNATURE_WORKSHOPS = [
  "Beyond the Calendar: Belonging as a Catalyst for Attendance, Engagement, and Retention",
  "Beyond the Buzzwords: Reimagining DEI as Practice, Not Performance",
  "Beyond the Buzzword: The Truth and Lies About Restorative Justice",
  "School Culture and Team-Building Professional Development",
  "School-Community Partnership Strategy",
  "Relationship-Based Safety and Student Voice Consulting",
  "Community Partner Onboarding and Readiness Support",
  "Restorative Practices and Peace Circles",
  "Student Leadership and Mentorship Program Design",
  "Higher Education to High School Student Voice Partnerships",
  "Student Voice and Narrative Workshops",
  "Student-Led Media and Narrative Platform Development",
  "Volunteer Pipeline Development",
  "Intergenerational and Family Engagement Initiatives",
];

export const PARTNERS = {
  schools: [
    "Butler College Prep",
    "Excel Academy of South Shore",
    "Excel Academy of Englewood",
    "Urban Prep",
    "Fenger High School",
    "Harlan Community Academy",
    "Burnham Academy",
    "Chicago Military Academy at Bronzeville",
    "Horace Mann",
    "Heritage Middle School",
    "Bloom Trail High School",
    "Morgan Park High School",
    "Kenwood Academy",
    "Simeon Career Academy",
  ],
  higherEducation: [
    "Trinity Christian College",
    "DePaul University (student volunteers)",
  ],
  community: [
    "After School Matters",
    "Youth Guidance",
    "Goldin Institute / Chicago Peace Fellows",
    "PNC Bank",
    "Southside STEM Station",
    "The Learning Tree",
    "Rosemoor Community Association",
    "Fields Creative Studios",
    "Flash Hoops",
    "Black Kids Predict",
  ],
};

export const RECOGNITION = [
  "Chicago Peace Fellow, Goldin Institute",
  "Teach2Give Honoree, ChiGivesBack",
  "2026 Blacktopia Gala Honoree, Association Chicago",
  "Black Educational Advocacy Coalition Conference Presenter",
  "Featured in The Regional News / Southwest Regional Publishing",
  "Keynote Speaker and Panelist",
];

export const PRESS = [
  {
    title: "Chicago Leader Reshapes Futures Through Elevate Greatness",
    publication: "Southwest Regional Publishing / The Regional News",
    date: "May 24, 2025",
    url:
      "http://southwestregionalpublishing.com/2025/05/24/chicago-leader-reshapes-futures-through-elevate-greatness/",
    excerpt:
      "A profile on Wanakee Trask and the work of Elevate Greatness in shaping outcomes for Chicago youth.",
  },
];

export const AUDIENCE_ROUTER = [
  {
    id: "schools",
    label: "Schools & Districts",
    description:
      "For district leaders, school administrators, and education teams strengthening climate, belonging, safety, and partnerships.",
    to: "/services?view=schools",
  },
  {
    id: "organizations",
    label: "Organizations & Communities",
    description:
      "For nonprofits, higher education partners, civic and corporate organizations, and community-serving teams.",
    to: "/services?view=organizations",
  },
];

export const CLIENT_TYPES = ["District", "School", "Organization", "Community", "Company"];

export const HOLDS_BUDGET_OPTIONS = ["Yes", "No", "Not sure"];

export const SOCIAL_LABELS = {
  instagram_url: "Instagram",
  linkedin_url: "LinkedIn",
  facebook_url: "Facebook",
};
